import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import {
  Box,
  Button,
  Chip,
  CircularProgress,
  Divider,
  Modal,
  TextField,
  Typography
} from '@mui/material';

import LoadingButton from '../../../components/buttons/Button';
import Copyable from '../../../components/utils/Copyable';
import NA from '../../../components/utils/NA';

import {
  GetErrorLogBIZ,
  ResolveErrorLogBIZ
} from '../../../redux/actions/business/errorLogsActions';

import style from "../../../global.scss";

const LABEL_COLOR = 'rgba(226, 242, 249, 0.6)';

// one label/value pair; `mono` is for ids, paths and endpoints
const Field = ({ label, value, mono, copyable, span }) => (
  <Box sx={{ gridColumn: span ? "1 / -1" : "auto", minWidth: 0 }}>
    <Typography variant='caption' sx={{ color: LABEL_COLOR, display: "block" }}>
      {label}
    </Typography>
    {
      value
      ?
      <Box
        sx={{
          fontFamily: mono ? "monospace" : "inherit",
          fontSize: mono ? "12px" : "14px",
          wordBreak: "break-word",
        }}>
        {copyable ? <Copyable>{String(value)}</Copyable> : String(value)}
      </Box>
      :
      <NA />
    }
  </Box>
);

// scrollable, monospaced block for stack traces and JSON payloads
const CodeBlock = ({ label, value }) => {
  if (!value) return null;

  let pretty = value;
  try {
    pretty = JSON.stringify(JSON.parse(value), null, 2);
  } catch (err) {
    // not JSON (a stack trace, most often) — show it as-is
  }

  return (
    <Box sx={{ gridColumn: "1 / -1" }}>
      <Typography variant='caption' sx={{ color: LABEL_COLOR, display: "block", mb: "4px" }}>
        {label}
      </Typography>
      <Box
        component="pre"
        sx={{
          margin: 0,
          padding: "12px",
          maxHeight: "220px",
          overflow: "auto",
          borderRadius: "8px",
          border: "1px solid rgba(44, 45, 47, 1)",
          backgroundColor: "rgba(20, 21, 23, 0.9)",
          fontSize: "12px",
          lineHeight: 1.5,
          whiteSpace: "pre-wrap",
          wordBreak: "break-word",
        }}>
        {pretty}
      </Box>
    </Box>
  );
};

const ErrorLogsDetailsModal = ({
  open,
  setOpen,
  errorLogId,
  severityColor,
  environmentColor,
  handleGet
}) => {
  const dispatch = useDispatch();
  const errorLog = useSelector(state => state.business.errorLog);
  const authUser = useSelector(state => state.auth.user);
  const [loading, setLoading] = useState(false);
  const [resolveLoading, setResolveLoading] = useState(false);
  const [notes, setNotes] = useState("");

  const handleClose = () => {
    setOpen(false);
    setNotes("");
  };

  useEffect(() => {
    if(open && errorLogId){
      dispatch(GetErrorLogBIZ(errorLogId, setLoading));
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, errorLogId]);

  const handleResolve = () => {
    // the controller falls back to "Admin" — sending the signed-in user makes
    // the audit trail actually say who cleared the error
    const resolvedBy = [authUser?.first_name, authUser?.last_name]
      .filter(Boolean)
      .join(" ") || authUser?.user_name || undefined;

    dispatch(ResolveErrorLogBIZ(errorLogId, { notes: notes, resolved_by: resolvedBy }, setResolveLoading)).then(() => {
      handleGet().then(() => {
        handleClose();
      });
    });
  };

  // the list row and the detail response share a shape; prefer the fetched one
  const row = errorLog && errorLog.id === errorLogId ? errorLog : null;

  return (
    <Modal
      open={open}
      onClose={handleClose}
      aria-labelledby="parent-modal-title"
      aria-describedby="parent-modal-description"
    >
      <Box className="flexCenterCenterRow"
        width={"100vw"} height={"100vh"}>
        <Box maxWidth={"md"} width={"90vw"} className="modal">

          {
            loading || !row
            ?
            <Box height={"300px"} className="flexCenterCenterRow">
              <CircularProgress />
            </Box>
            :
            <Box
              sx={{ padding: "20px", maxHeight: "75vh", overflowY: "auto" }}>

              <Box className="flexCenter_Row" gap={"10px"} mb={"6px"} flexWrap={"wrap"}>
                <Chip size="small" label={row.severity || 'N/A'} color={severityColor(row.severity)} variant="filled" />
                <Chip size="small" label={row.environment || 'N/A'} color={environmentColor(row.environment)} variant="outlined" />
                <Chip size="small" label={row.is_resolved ? 'Resolved' : 'Pending'} color={row.is_resolved ? 'success' : 'error'} variant="filled" />
                <Chip size="small" label={row.request_method || 'N/A'} color="primary" variant="outlined" />
              </Box>

              <Typography variant='h6' sx={{ color: style["primary"], wordBreak: "break-word" }}>
                {row.error_message || "Error"}
              </Typography>
              <Typography variant='caption' sx={{ color: LABEL_COLOR }}>
                <Copyable>{String(row.id || "")}</Copyable>
              </Typography>

              <Divider sx={{ backgroundColor: "rgba(44, 45, 47, 1)", margin: "16px 0px" }}/>

              <Box
                sx={{
                  display: "grid",
                  gridTemplateColumns: "repeat(auto-fit, minmax(220px, 1fr))",
                  gap: "14px",
                }}>
                <Field label="Application" value={row.application_name} />
                <Field label="Error code" value={row.error_code} mono />
                <Field label="Function" value={row.function_name} mono />
                <Field label="File path" value={row.file_path} mono span />
                <Field label="Endpoint" value={row.endpoint} mono span />
                <Field label="Company id" value={row.ref_company_id} mono copyable />
                <Field label="User id" value={row.ref_user_id} mono copyable />
                <Field label="IP address" value={row.ip_address} mono />
                <Field label="Occurred at" value={row.createdAt && new Date(row.createdAt).toString().substring(0, 33)} />
                <Field label="User agent" value={row.user_agent} span />

                <CodeBlock label="Stack trace" value={row.error_stack} />
                <CodeBlock label="Request query" value={row.request_query} />
                <CodeBlock label="Request body" value={row.request_body} />
                <CodeBlock
                  label="Additional context"
                  value={row.additional_context ? JSON.stringify(row.additional_context) : null}
                  />
              </Box>

              <Divider sx={{ backgroundColor: "rgba(44, 45, 47, 1)", margin: "16px 0px" }}/>

              {
                row.is_resolved
                ?
                <Box
                  sx={{
                    display: "grid",
                    gridTemplateColumns: "repeat(auto-fit, minmax(220px, 1fr))",
                    gap: "14px",
                  }}>
                  <Field label="Resolved by" value={row.resolved_by} />
                  <Field label="Resolved at" value={row.resolved_at && new Date(row.resolved_at).toString().substring(0, 33)} />
                  <Field label="Notes" value={row.notes} span />
                </Box>
                :
                <Box>
                  <Typography variant='caption' sx={{ color: LABEL_COLOR, display: "block", mb: "8px" }}>
                    Mark this error as resolved
                  </Typography>
                  <TextField
                    label="Resolution notes"
                    fullWidth
                    multiline
                    minRows={2}
                    value={notes}
                    onChange={(e) => setNotes(e.target.value)}
                    />
                </Box>
              }
            </Box>
          }

          <Divider sx={{ backgroundColor: "rgba(44, 45, 47, 1)" }}/>
          <Box className="flexCenterFERow" sx={{ gap: "20px", margin: "15px"}}>
            <Button variant='outlined'
              onClick={handleClose}>
              Close
            </Button>
            {
              row && !row.is_resolved
              &&
              <LoadingButton
                variant='contained'
                loading={resolveLoading}
                onClick={handleResolve}>
                Mark resolved
              </LoadingButton>
            }
          </Box>
        </Box>
      </Box>
    </Modal>
  )
}

export default ErrorLogsDetailsModal;
